import { announce } from "@kovernow/flows/announce";
import { homeQueryKeys } from "@kovernow/flows/home";
import { toRecoveryAction, type RecoveryAction } from "@kovernow/flows/recovery";
import { ownStorageLocation, vaultQueryKeys, type VaultItem } from "@kovernow/flows/vault";
import { Button } from "@kovernow/react-ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@kovernow/react-ui/components/dialog";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";
import { Recovery } from "../../components/Recovery";
import { useServices } from "../../core/app-context";
import { useMutationGate } from "../../core/mutation-gate";
import { useT } from "../../core/use-t";
import { isItemHasPolicy, itemHasPolicyAction } from "./shared";
import type { ApiError } from "@kovernow/flows/recovery";

/**
 * "Delete {name}?": the consequence, Cancel and Delete. Delete sends
 * `DELETE /items/:id`; only after the 2xx are the item's own Storage
 * objects deleted (best effort, `users/{uid}/` only), the item's query
 * dropped and the Vault/Home refreshed. A failure keeps the dialog open
 * with a RecoveryView; `item_has_policy` also tells the screen to refetch.
 */
export function DeleteItemDialog({
  item,
  name,
  ownerId,
  open,
  onOpenChange,
  onHasPolicy,
}: {
  item: VaultItem;
  name: string;
  ownerId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 409 `item_has_policy`: show the notice and refetch the item. */
  onHasPolicy: () => void;
}) {
  const t = useT();
  const { vaultApi, uploads } = useServices();
  const gate = useMutationGate();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [recovery, setRecovery] = useState<RecoveryAction | null>(null);

  const confirm = async () => {
    if (busy || gate.blocked) return;
    setBusy(true);
    setRecovery(null);
    announce(t("itemDetail.announce.deleting", { name }));
    try {
      await vaultApi.remove(item._id);
    } catch (err) {
      setBusy(false);
      if (isItemHasPolicy(err)) {
        setRecovery(itemHasPolicyAction(err as ApiError));
        onHasPolicy();
      } else {
        setRecovery(toRecoveryAction(err));
      }
      return;
    }
    // The server confirmed: now the Storage objects (best effort, own prefix only).
    const locations = (item.documents ?? [])
      .map((d) => ownStorageLocation(d, ownerId))
      .filter((l): l is string => !!l);
    await Promise.allSettled(locations.map((l) => uploads.remove(ownerId, l)));
    queryClient.removeQueries({ queryKey: vaultQueryKeys.item(item._id), exact: true });
    void queryClient.invalidateQueries({ queryKey: vaultQueryKeys.items() });
    void queryClient.invalidateQueries({ queryKey: homeQueryKeys.dashboard() });
    announce(t("itemDetail.announce.deleted", { name }));
    navigate("/vault");
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (busy) return;
        if (!next) setRecovery(null);
        onOpenChange(next);
      }}
    >
      <DialogContent showCloseButton={false} data-testid="delete-dialog">
        <DialogHeader>
          <DialogTitle>{t("itemDetail.delete.title", { name })}</DialogTitle>
          <DialogDescription>{t("itemDetail.delete.body")}</DialogDescription>
        </DialogHeader>
        {recovery ? (
          <div data-testid="delete-error">
            <Recovery action={recovery} onRetry={() => void confirm()} />
          </div>
        ) : null}
        <DialogFooter>
          <Button type="button" variant="outline" size="touch" onClick={() => !busy && onOpenChange(false)}>
            {t("itemDetail.delete.cancel")}
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="touch"
            data-testid="confirm-delete"
            aria-disabled={busy || gate.blocked ? true : undefined}
            onClick={() => void confirm()}
          >
            {busy ? t("common.loading") : t("itemDetail.delete.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
